import { useEffect, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import { useInView } from 'react-intersection-observer';
import { withOptimizely } from '@optimizely/react-sdk';

import { Select } from '@klv/molecules/form-field';
import { SelectArrow } from '@klv/molecules/form-field/select';
import Modal from '@klv/organisms/modal';
import Grid from '@klv/utils/grid';
import { useModal } from '@klv/context/modal';
import { windowIsDefined } from '@klv/shared/utils/functions';
import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';

import MaxContactsElement from './max-contacts';
import PricingModalContentElement from './modal-content';

import pricingData from '@klv/shared/pricing/static/us.json';
import staticData from './stories/data';

import * as s from './styles';
import ComparisonChart from '../comparison-chart';

import type { WithOptimizelyProps } from '@optimizely/react-sdk';
import type { IEmailPlan, IPricing, ISMSPlan } from './types';

type TEmailPlans = keyof typeof pricingData['email'];

type TSmsPlans = keyof typeof pricingData['sms'];

const generateMinMaxString = (plan: IEmailPlan, locale: Intl.LocalesArgument = 'en-US') => `${plan.min_customers.toLocaleString(locale)} - ${plan.max_customers.toLocaleString(locale)}`;

const Pricing = ({
  optimizely,
  optimizelyReadyTimeout,
  isServerSide,
  backgroundColor = 'dark',
  cardContent,
  MaxContacts,
  PricingModalContent,
  comparisonChart,
  excludeFree,
  cardOrder = 0,
  ...props
}: IPricing & WithOptimizelyProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const modalStore = useModal();
  const { modalState } = modalStore;
  const [contacts, setContacts] = useState(1001);
  const [smsCredits, setSmsCredits] = useState(1250);
  const [isSms, setIsSms] = useState(true);
  const [selectedLocale, setSelectedLocale] = useState('US');

  const { ref, inView } = useInView({
    threshold: 1,
  });
  const headerStuck = !inView;

  const emailPlans = pricingData.email;
  const emailPlanKeys = Object.keys(emailPlans).map((key) => Number(key)).sort((a, b) => a - b);
  const smsPlans = pricingData.sms;

  const selectedPlan: IEmailPlan = emailPlans[contacts.toString() as TEmailPlans];
  const selectedPlanMinMaxString = generateMinMaxString(selectedPlan, locale);
  const selectedSmsPlan: ISMSPlan = smsPlans[smsCredits.toString() as TSmsPlans];

  const {
    emailAndSms: {
      buttons: emailAndSmsButtons = [],
    } = {},
    email: {
      buttons: emailButtons = [],
    } = {},
    free: {
      buttons: freeButtons = [],
    } = {},
  } = cardContent || {};

  useEffect(() => {
    const initialParams = new URLSearchParams(window.location.search);
    const paramContacts = Number(initialParams.get('contacts'));
    const paramSmsCredits = Number(initialParams.get('smsCredits'));

    if (paramContacts && emailPlans?.[paramContacts.toString() as TEmailPlans]) {
      setContacts(paramContacts);
    }

    if (paramSmsCredits && smsPlans?.[paramSmsCredits.toString() as TSmsPlans]) {
      setSmsCredits(paramSmsCredits);
    }
  }, [emailPlans, smsPlans]);

  const handleChange = (value: string, context: 'contacts' | 'smsCredits') => {
    if (windowIsDefined()) {
      const url = new URL(window.location.href);
      const setter = context === 'contacts' ? setContacts : setSmsCredits;

      url.searchParams.set(context, value);
      setter(Number(value));

      window.history.pushState({}, '', url);
    }
  };

  const openSmsModal = (label: 'email and sms' | 'email' | 'free') => {
    modalStore.open('sms-mms-credits');
    gtmBasicEvent({
      category: 'Pricing',
      action: 'credit modal',
      label: label,
    });
  };

  // Optimizely test "2024q3_pricing_stat_card" 
  const decision = optimizely?.decide('2024q3_pricing_stat_card');
  const hasStatCardTest = decision?.enabled || false;

  // The cards have been separated into their own components to make their order flexible for AB testing
  const FreeCard = () => {
    if (!excludeFree || contacts === 0) {
      return (<s.StyledPlanCard
        buttonLink={(freeButtons?.[0]?.url) || t('organisms_navigation_dataActions_signUp_button_href')}
        buttonOpenInNewTab={(freeButtons?.[0]?.openInNewTab) || undefined}
        buttonText={(freeButtons?.[0]?.text) || (t('organisms_pricing_signUp') || 'Sign up')}
        contacts={contacts}
        contactsString={selectedPlanMinMaxString}
        contactLimit={250}
        copy={t('organisms_pricing_plans_free_copy')}
        id="pricing--plan-card--free"
        displayMobilePush={false}
        emailSends={500}
        freeCredits={150}
        name={t('organisms_pricing_plans_free_name')}
        price={0}
        topCalloutText={t('organisms_pricing_plans_free_calloutText') || undefined}
        isSms={isSms}
        setIsSms={setIsSms}
        selectedLocale={selectedLocale}
        setSelectedLocale={setSelectedLocale}
      />);
    } else if (contacts === 0) {
      return <><s.StyledSpacer /><FreeCard/></>;
    }
  };

  const EmailCard = () => {
    return contacts > emailPlanKeys[0] ? (
      <s.StyledPlanCard
        buttonLink={(emailButtons?.[0]?.url) || t('organisms_navigation_dataActions_signUp_button_href')}
        buttonOpenInNewTab={(emailButtons?.[0]?.openInNewTab) || undefined}
        buttonText={(emailButtons?.[0]?.text) || (t('organisms_pricing_signUp') || 'Sign up')}
        contacts={contacts}
        contactsString={selectedPlanMinMaxString}
        copy={t('organisms_pricing_plans_email_copy')}
        id="pricing--plan-card--email"
        emailSends={selectedPlan.send_limit}
        freeCredits={150}
        name={t('organisms_pricing_plans_email_name')}
        price={selectedPlan.price}
        topCalloutText={t('organisms_pricing_plans_email_calloutText') || undefined}
        isSms={isSms}
        setIsSms={setIsSms}
        selectedLocale={selectedLocale}
        setSelectedLocale={setSelectedLocale}
      />
    ) : '';
  };

  const EmailAndSMSCard = () => {
    return contacts > emailPlanKeys[0] ? (
      <s.StyledPlanCard
        backgroundColor={backgroundColor}
        smsCredits={smsCredits}
        isSms={isSms}
        setIsSms={setIsSms}
        selectedLocale={selectedLocale}
        setSelectedLocale={setSelectedLocale}
        buttonLink={(emailAndSmsButtons?.[0]?.url) || t('organisms_navigation_dataActions_signUp_button_href')}
        buttonOpenInNewTab={(emailAndSmsButtons?.[0]?.openInNewTab) || undefined }
        buttonText={(emailAndSmsButtons?.[0]?.text) || (t('organisms_pricing_signUp') || 'Sign up')}
        contacts={contacts}
        contactsString={selectedPlanMinMaxString}
        copy={hasStatCardTest ? 'Take your marketing further. Brands with email + SMS see a 19% increase in GMV growth rate.' : t('organisms_pricing_plans_emailAndSms_copy')}
        id="pricing--plan-card--email-and-sms"
        emailSends={selectedPlan.send_limit}
        name={t('organisms_pricing_plans_emailAndSms_name')}
        price={selectedPlan.price + selectedSmsPlan.price}
        topCalloutText={t('organisms_pricing_plans_emailAndSms_calloutText') || undefined}
      >
        <s.PlanCardDropdownWrapper>
          <s.Dropdown backgroundColor={backgroundColor}>
            <SelectArrow backgroundColor={backgroundColor} iconName="ChevronDown" fillColor="currentColor" />
            <Select $backgroundColor={backgroundColor} 
              aria-labelledby="pricing--sms-credits"
              hasError={false}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>) => handleChange(event.target.value, 'smsCredits')}
              value={smsCredits}
            >
              {Object.keys(smsPlans).map((sendLimit) => <option key={sendLimit} value={sendLimit}>{Number(sendLimit).toLocaleString(locale)}</option>)}
            </Select>
          </s.Dropdown>
          <s.ModalTrigger
            aria-label={`${smsCredits} ${t('organisms_pricing_monthlySmsMmsCredits')} ${t('organisms_pricing_opensAModal')}`}
            forwardedAs="button"
            id="pricing--sms-credits"
            onClick={() => openSmsModal('email and sms')}
            variant="tagline"
          >
            {t('organisms_pricing_monthlySmsMmsCredits')}
            <s.InformationIcon fillColor="currentColor" iconName="InformationCircle" size={1.8} />
          </s.ModalTrigger>
        </s.PlanCardDropdownWrapper>
      </s.StyledPlanCard>
    ) : '';
  };


  return (
    <s.StyledPricing {...props}>
      <style type="text/css">
        {`
          @media only screen and (max-width: 1023px) {
            html {
              scroll-padding-top: 16rem;
            }
          }
        `}
      </style>
      <s.Header ref={ref} $stuck={headerStuck}>
        <Grid>
          <s.HeaderContent $centered={!!excludeFree}>
            <s.StyledEyebrow forwardedAs="div" variant="tagline">{t('organisms_pricing_eyebrow')}</s.StyledEyebrow>
            <s.DropdownWrapper>
              <s.StyledHeadline forwardedAs="h2" id="pricing--contacts-headline" variant={headerStuck ? 'h5' : 'h3'}>
                {t('organisms_pricing_headline')}
              </s.StyledHeadline>
              <s.Dropdown backgroundColor={backgroundColor} >
                <SelectArrow backgroundColor={backgroundColor} iconName="ChevronDown" fillColor="currentColor" />
                <Select $backgroundColor={backgroundColor} 
                  aria-labelledby="pricing--contacts-headline"
                  hasError={false}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => handleChange(event.target.value, 'contacts')}
                  value={contacts}
                >
                  {emailPlanKeys.map((planKey) => (
                    (emailPlans[planKey.toString() as TEmailPlans].min_customers >= 0)
                      ? <option key={planKey} value={planKey}>{generateMinMaxString(emailPlans[planKey.toString() as TEmailPlans], locale)}</option>
                      : <option key={planKey} value={planKey}>{`${planKey.toLocaleString(locale)}${t('organisms_pricing_plusSign')}`}</option>
                  ))}
                </Select>
              </s.Dropdown>
            </s.DropdownWrapper>
          </s.HeaderContent>
        </Grid>
      </s.Header>
      {contacts < emailPlanKeys[emailPlanKeys.length - 1] && (
        <s.Plans>
          {contacts > emailPlanKeys[0] && (
            <s.AnchorLinks>
              <s.AnchorLink href="#pricing--plan-card--email-and-sms" forwardedAs="a" variant="paragraph1">{t('organisms_pricing_plans_emailAndSms_name')}</s.AnchorLink>
              <s.AnchorLink href="#pricing--plan-card--email" forwardedAs="a" variant="paragraph1">{t('organisms_pricing_plans_email_name')}</s.AnchorLink>
              <s.AnchorLink href="#pricing--plan-card--free" forwardedAs="a" variant="paragraph1">{t('organisms_pricing_plans_free_name')}</s.AnchorLink>
            </s.AnchorLinks>
          )}
          {excludeFree && <s.StyledSpacer />}
          {/**
           * The order of the cards is determined by the cardOrder prop
           */}
          {[
            (<><EmailAndSMSCard /><EmailCard /><FreeCard /></>),
            (<><EmailAndSMSCard /><FreeCard /><EmailCard /></>),
            (<><EmailCard /><EmailAndSMSCard /><FreeCard /></>),
            (<><EmailCard /><FreeCard /><EmailAndSMSCard /></>),
            (<><FreeCard /><EmailAndSMSCard /><EmailCard /></>),
            (<><FreeCard /><EmailCard /><EmailAndSMSCard /></>),
          ][cardOrder || 0]}
        </s.Plans>
      )}
      {contacts === emailPlanKeys[emailPlanKeys.length - 1] && (MaxContacts || <MaxContactsElement mixedMedia={staticData.mixedMedia} />)}
      <Modal
        close={modalStore.close}
        contentPadding="3.6rem"
        show={modalState.visibleModalId === 'sms-mms-credits'}
        size="constrained"
        backgroundColor="lightSand"
      >
        {comparisonChart?.chart ? <ComparisonChart {...comparisonChart}/> : <PricingModalContentElement />}  
      </Modal>
    </s.StyledPricing>
  );
};

export default withOptimizely(Pricing);
